/* External dependencies */
import ISignUpResult from '@mancho.devs/cognito';
import { CognitoSigninResponse } from '@mancho.devs/cognito/lib/cognito-types';
import { CognitoUserSession } from 'amazon-cognito-identity-js';

export enum SignUpActionTypes {
  SIGNUP = 'SIGNUP',
  SIGNUP_RESET = 'SIGNUP_RESET',
  SIGNUP_FAILED = 'SIGNUP_FAILED',
  SIGNUP_SUCCEEDED = 'SIGNUP_SUCCEEDED',
}

export enum SignInActionTypes {
  SIGNIN = 'SIGNIN',
  SIGNIN_FAILED = 'SIGNIN_FAILED',
  SIGNIN_SUCCEEDED = 'SIGNIN_SUCCEEDED',
}

export enum SignOutActionTypes {
  SIGNOUT = 'SIGNOUT',
  SIGNOUT_FAILED = 'SIGNOUT_FAILED',
  SIGNOUT_TOGGLE = 'SIGNOUT_TOGGLE',
  SIGNOUT_SUCCEEDED = 'SIGNOUT_SUCCEEDED',
}

export enum ConfirmCodeActionTypes {
  CONFIRM_CODE = 'CONFIRM_CODE',
  CONFIRM_CODE_FAILED = 'CONFIRM_CODE_FAILED',
  CONFIRM_CODE_SUCCEEDED = 'CONFIRM_CODE_SUCCEEDED',
}

export enum PasswordForgotActionTypes {
  PASSWORD_FORGOT = 'PASSWORD_FORGOT',
  PASSWORD_FORGOT_FAILED = 'PASSWORD_FORGOT_FAILED',
  PASSWORD_FORGOT_SUCCEEDED = 'PASSWORD_FORGOT_SUCCEEDED',
}

export enum ConfirmPasswordActionTypes {
  PASSWORD_CONFIRM = 'PASSWORD_CONFIRM',
  PASSWORD_CONFIRM_SUCCEEDED = 'PASSWORD_CONFIRM_SUCCEEDED',
  PASSWORD_CONFIRM_FAILED = 'PASSWORD_CONFIRM_FAILED',
}

export enum GetSessionActionTypes {
  GET_SESSION = 'GET_SESSION',
  GET_SESSION_SUCCEEDED = 'GET_SESSION_SUCCEEDED',
  GET_SESSION_FAILED = 'GET_SESSION_FAILED',
}

export interface SignUpRequest {
  type: SignUpActionTypes.SIGNUP;
  email: string;
  password: string;
}

export interface SignUpSucceeded {
  type: SignUpActionTypes.SIGNUP_SUCCEEDED;
  result: ISignUpResult;
}

export interface SignUpFailed {
  type: SignUpActionTypes.SIGNUP_FAILED;
  error: Error;
}

export interface SignUpReset {
  type: SignUpActionTypes.SIGNUP_RESET;
}

export interface ConfirmCode {
  type: ConfirmCodeActionTypes.CONFIRM_CODE;
  email: string;
  code: string;
}

export interface ConfirmCodeSucceeded {
  type: ConfirmCodeActionTypes.CONFIRM_CODE_SUCCEEDED;
  confirmCodeStatus: string;
}

export interface ConfirmCodeFailed {
  type: ConfirmCodeActionTypes.CONFIRM_CODE_FAILED;
  error: Error;
}

export interface SignInRequest {
  type: SignInActionTypes.SIGNIN;
  email: string;
  password: string;
}

export interface SignInSucceeded {
  type: SignInActionTypes.SIGNIN_SUCCEEDED;
  result: CognitoSigninResponse;
}

export interface SignInFailed {
  type: SignInActionTypes.SIGNIN_FAILED;
  signInError: Error;
}

export interface SignOutToggle {
  type: SignOutActionTypes.SIGNOUT_TOGGLE;
}

export interface SignOutRequest {
  type: SignOutActionTypes.SIGNOUT;
}

export interface SignOutSucceeded {
  type: SignOutActionTypes.SIGNOUT_SUCCEEDED;
}

export interface SignOutFailed {
  type: SignOutActionTypes.SIGNOUT_FAILED;
  error: Error;
}

export interface PasswordForgotRequest {
  type: PasswordForgotActionTypes.PASSWORD_FORGOT;
  email: string;
}

export interface PasswordForgotSucceeded {
  type: PasswordForgotActionTypes.PASSWORD_FORGOT_SUCCEEDED;
}

export interface PasswordForgotFailed {
  type: PasswordForgotActionTypes.PASSWORD_FORGOT_FAILED;
  error: Error;
}

export interface ConfirmPasswordRequest {
  type: ConfirmPasswordActionTypes.PASSWORD_CONFIRM;
  email: string;
  pinCode: string;
  newPassword: string;
}

export interface ConfirmPasswordSucceeded {
  type: ConfirmPasswordActionTypes.PASSWORD_CONFIRM_SUCCEEDED;
}

export interface ConfirmPasswordFailed {
  type: ConfirmPasswordActionTypes.PASSWORD_CONFIRM_FAILED;
  error: Error;
}

export interface GetSessionRequest {
  type: GetSessionActionTypes.GET_SESSION;
}

export interface GetSessionSucceeded {
  type: GetSessionActionTypes.GET_SESSION_SUCCEEDED;
  email: string;
  session: CognitoUserSession,
}

export interface GetSessionFailed {
  type: GetSessionActionTypes.GET_SESSION_FAILED;
  error: Error;
}

export function signUp(email: string, password: string): SignUpRequest {
  return {
    type: SignUpActionTypes.SIGNUP,
    email,
    password,
  };
}

export function signUpSucceeded(result: ISignUpResult): SignUpSucceeded {
  return {
    type: SignUpActionTypes.SIGNUP_SUCCEEDED,
    result,
  };
}

export function signUpFailed(error: Error): SignUpFailed {
  return {
    type: SignUpActionTypes.SIGNUP_FAILED,
    error,
  };
}

export function setSignUpReset(): SignUpReset {
  return {
    type: SignUpActionTypes.SIGNUP_RESET,
  };
}

export function setConfirmCode(email: string, code: string): ConfirmCode {
  return {
    type: ConfirmCodeActionTypes.CONFIRM_CODE,
    email,
    code,
  };
}

export function signUpConfirmCodeSucceeded(confirmCodeStatus: string): ConfirmCodeSucceeded {
  return {
    type: ConfirmCodeActionTypes.CONFIRM_CODE_SUCCEEDED,
    confirmCodeStatus,
  };
}

export function signUpConfirmCodeFailed(error: Error): ConfirmCodeFailed {
  return {
    type: ConfirmCodeActionTypes.CONFIRM_CODE_FAILED,
    error,
  };
}

export function signIn(email: string, password: string): SignInRequest {
  return {
    type: SignInActionTypes.SIGNIN,
    email,
    password,
  };
}

export function signInSucceeded(result: CognitoSigninResponse): SignInSucceeded {
  return {
    type: SignInActionTypes.SIGNIN_SUCCEEDED,
    result,
  };
}

export function signInFailed(signInError: Error): SignInFailed {
  return {
    type: SignInActionTypes.SIGNIN_FAILED,
    signInError,
  };
}

export function signOutToggle(): SignOutToggle {
  return {
    type: SignOutActionTypes.SIGNOUT_TOGGLE,
  };
}

export function signOut(): SignOutRequest {
  return {
    type: SignOutActionTypes.SIGNOUT,
  };
}

export function signOutSucceeded(): SignOutSucceeded {
  return {
    type: SignOutActionTypes.SIGNOUT_SUCCEEDED,
  };
}

export function signOutFailed(error: Error): SignOutFailed {
  return {
    type: SignOutActionTypes.SIGNOUT_FAILED,
    error,
  };
}

export function passwordForgot(email: string): PasswordForgotRequest {
  return {
    type: PasswordForgotActionTypes.PASSWORD_FORGOT,
    email,
  };
}

export function passwordForgotSucceeded(): PasswordForgotSucceeded {
  return {
    type: PasswordForgotActionTypes.PASSWORD_FORGOT_SUCCEEDED,
  };
}

export function passwordForgotFailed(error: Error): PasswordForgotFailed {
  return {
    type: PasswordForgotActionTypes.PASSWORD_FORGOT_FAILED,
    error,
  };
}

export function confirmPassword(email: string, pinCode: string, newPassword: string): ConfirmPasswordRequest {
  return {
    type: ConfirmPasswordActionTypes.PASSWORD_CONFIRM,
    email,
    pinCode,
    newPassword,
  };
}

export function confirmPasswordSucceeded(): ConfirmPasswordSucceeded {
  return {
    type: ConfirmPasswordActionTypes.PASSWORD_CONFIRM_SUCCEEDED,
  };
}

export function confirmPasswordFailed(error: Error): ConfirmPasswordFailed {
  return {
    type: ConfirmPasswordActionTypes.PASSWORD_CONFIRM_FAILED,
    error,
  };
}

export function getSession(): GetSessionRequest {
  return {
    type: GetSessionActionTypes.GET_SESSION,
  };
}

export function getSessionSucceeded(email: string, session: CognitoUserSession): GetSessionSucceeded {
  return {
    type: GetSessionActionTypes.GET_SESSION_SUCCEEDED,
    email,
    session,
  };
}

export function getSessionFailed(error: Error): GetSessionFailed {
  return {
    type: GetSessionActionTypes.GET_SESSION_FAILED,
    error,
  };
}

export type AuthAction =
  | SignUpReset
  | SignUpFailed
  | SignUpRequest
  | SignUpSucceeded
  | ConfirmCode
  | ConfirmCodeFailed
  | ConfirmCodeSucceeded
  | SignInRequest
  | SignInSucceeded
  | SignInFailed
  | SignOutToggle
  | SignOutRequest
  | SignOutSucceeded
  | SignOutFailed
  | PasswordForgotRequest
  | PasswordForgotSucceeded
  | PasswordForgotFailed
  | ConfirmPasswordRequest
  | ConfirmPasswordSucceeded
  | ConfirmPasswordFailed
  | GetSessionRequest
  | GetSessionSucceeded
  | GetSessionFailed;
